import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getPayrocSessionToken } from 'api/Transaction';

import { achFields, achWrapperMap, cardFields, cardWrapperMap } from '../lib/fields';
import { achStyles, cardStyles } from '../lib/styles';

const PAYROC_HOSTED_FIELDS_SCRIPT = process.env.REACT_APP_PAYROC_HOSTED_FIELDS_SCRIPT;

export const usePayroc = ({
  type,
  containerRef,
  amount,
  handlePayment,
  validate,
  id,
  mode = 'payment',
  buttonText,
  secureTokenId,
  terminalId,
  onError = () => {}
}) => {
  const [_dataBToken, setDataBToken] = useState(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldFormCard, setFieldFormCard] = useState(null);
  const [fieldFormACH, setFieldFormACH] = useState(null);

  const handlePaymentRef = useRef(handlePayment);
  const validateRef = useRef(validate);

  useEffect(() => {
    handlePaymentRef.current = handlePayment;
  }, [handlePayment]);

  useEffect(() => {
    validateRef.current = validate;
  }, [validate]);

  const isCard = type === 'card';
  const isAch = type === 'ach';

  const navigate = useNavigate();
  const script = document.createElement('script');

  useEffect(() => {
    try {
      script.src = PAYROC_HOSTED_FIELDS_SCRIPT;
      script.async = true;
      script.onload = () => setScriptLoaded(true);
      script.onerror = () => console.error('Failed to load 3rd-party script');
      document.body.appendChild(script);
    } catch (error) {
      console.error('Error loading 3rd-party script:', error);
      setError(true);
    }

    return () => {
      document.body.removeChild(script);
    };
  }, [id]);

  const initializeForm = useCallback(
    async (formFields, wrapperMap) => {
      const Payroc = window.Payroc;

      try {
        setIsLoading(true);
        const res = await getPayrocSessionToken(navigate, {
          scenario: mode,
          secureTokenId,
          terminalId
        });
        const sessionToken = res?.token;

        if (!sessionToken) {
          onError('An unexpected error has occurred. Please try again later.');
          return;
        }

        setDataBToken(sessionToken);

        if (containerRef.current && Payroc.hostedFields && sessionToken) {
          const instance = new Payroc.hostedFields({
            ...formFields,
            sessionToken,
            onPreSubmit: async () => {
              if (validateRef.current) {
                const isValid = await validateRef.current();
                return isValid;
              }
              return true;
            }
          });

          await instance.initialize();

          instance.on('error', ({ type, field, message }) => {
            console.error('Error:', { type, field, message });
            setError({ error: message, type, field });
          });
          instance.on('submissionSuccess', async (data) => {
            if (!handlePaymentRef.current) {
              setError({
                error: 'Payment method not supported',
                type: 'error',
                field: 'payment_method'
              });
              return;
            }
            const result = await handlePaymentRef.current(data);
            if (result?.code !== 0) {
              setError(result);
              return;
            }
          });

          instance.on('fieldValidityChange', ({ field, validationError }) => {
            const element = document.querySelector(wrapperMap[field]);
            if (element) {
              if (validationError) {
                element.classList.add('has-error');
                return;
              }
              element.classList.remove('has-error');
            }
          });

          return instance;
        }
      } catch (err) {
        setError(true);
        onError(err.error || 'An unexpected error has occurred. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    },
    [containerRef]
  );

  const initCardForm = useCallback(async () => {
    if (fieldFormCard || !scriptLoaded) return;
    const formFields = {
      mode,
      fields: { card: cardFields({ amount, buttonText }) },
      styles: cardStyles
    };

    const hostedFieldsFormCard = await initializeForm(formFields, cardWrapperMap);

    setFieldFormCard(hostedFieldsFormCard);
  }, [initializeForm, amount, scriptLoaded, buttonText]);

  const initACHForm = useCallback(async () => {
    if (fieldFormACH || !scriptLoaded) return;
    const formFields = {
      mode,
      fields: { ach: achFields({ amount, buttonText }) },
      styles: achStyles
    };

    const hostedFieldsFormACH = await initializeForm(formFields, achWrapperMap);

    setFieldFormACH(hostedFieldsFormACH);
  }, [initializeForm, amount, scriptLoaded, buttonText]);

  const startInitialization = useCallback(() => {
    if (isAch) {
      initACHForm();
    } else if (isCard) {
      initCardForm();
    }
  }, [initACHForm, initCardForm, isCard, isAch]);

  useEffect(() => {
    startInitialization();
  }, [startInitialization]);

  const retry = useCallback(() => {
    setError(null);
    startInitialization();
  }, [startInitialization]);

  return {
    isLoading,
    error,
    retry
  };
};
